import React, { useEffect, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCookies } from "react-cookie";
import Service from "../../api/Service";
import User from "../../api/User";
import { TAG_COLORS } from "../../utils/tagColors";
import dayjs from "dayjs";
import { FONTS, CARD_STYLES } from "../../constants/styles";

interface Props {
    bookingId: string;
    userId: string;
    serviceId: string;
    start: dayjs.Dayjs;
    onDiscountChange: (discount: number) => void;
    currentDiscount: number;
}

const tagsColors = TAG_COLORS;

const ClientsReviewItem: React.FC<Props> = ({
    bookingId,
    userId,
    serviceId,
    start,
    onDiscountChange,
    currentDiscount,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [serviceName, setServiceName] = useState<string>("");
    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        Service.info(uuid, brand_id, serviceId)
            .then((res) => setServiceName(res?.name ?? "Service"))
            .catch(() => setServiceName("Service"));

        User.info(uuid, brand_id, userId)
            .then((res) => setUser(res))
            .catch(() => setUser(null));
    }, [uuid, brand_id, serviceId, userId]);

    const formatName = (name: string) => {
        const parts = name.split(" ");
        return parts.length > 1
            ? `${parts[0]} ${parts.slice(1).join(" ")}`
            : parts[0];
    };

    const tagColor = user
        ? tagsColors.find((tagObj) => tagObj.tag === user.status)?.color ??
          primary
        : primary;

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{ ...CARD_STYLES, opacity: user ? 1 : 0.4 }}
        >
            {/* Avatar */}
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    bgcolor: alpha(primary, 0.15),
                    flex: "0 0 auto",
                    backgroundImage: user?.image
                        ? `url(${user.image})`
                        : undefined,
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                }}
            />
            {/* Info column */}
            <Stack
                flex={1}
                justifyContent="center"
                spacing={0.25}
                ml={1.5}
                minWidth={0}
            >
                {/* Name + Status */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="15px"
                        noWrap
                    >
                        {user ? formatName(user.name) : "\u00A0"}
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: user?.status
                                ? alpha(tagColor, 0.12)
                                : "transparent",
                            px: 1,
                            py: 0.25,
                            borderRadius: "8px",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            fontWeight={500}
                            fontSize="11px"
                            color={tagColor}
                            lineHeight="16px"
                        >
                            {user?.status ?? "\u00A0"}
                        </Typography>
                    </Box>
                </Stack>

                {/* Service info */}
                <Typography fontSize="13px" color="text.secondary">
                    {user
                        ? `${serviceName} at ${start.format("HH:mm")}`
                        : "\u00A0"}
                </Typography>
            </Stack>

            {/* Discount selector */}
            <Select
                value={currentDiscount}
                onChange={(e) => onDiscountChange(Number(e.target.value))}
                size="small"
                sx={{
                    width: "76px",
                    ml: 1,
                    borderRadius: "10px",
                    bgcolor: alpha(primary, 0.04),
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(primary, 0.12),
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(primary, 0.25),
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: primary,
                    },
                }}
            >
                {[0, 5, 10, 15, 20, 25, 30, 40, 50].map((val) => (
                    <MenuItem
                        key={val}
                        value={val}
                        sx={{
                            fontFamily: FONTS.display,
                            fontWeight: 600,
                            fontSize: "14px",
                        }}
                    >
                        {val}%
                    </MenuItem>
                ))}
            </Select>
        </Stack>
    );
};

export default ClientsReviewItem;
